import { useCallback, useEffect, useState } from "react";
import { useMemberAuthStore } from "modules/members/auth";
import {
  DEFAULT_VISIBLE_COLUMNS,
  columnsForStorage,
  parseColumnPrefs,
  resolveSplitEntitlements,
  serializeColumnPrefs,
  toggleVisibleColumn,
} from "./reviewLogic";

/**
 * useReviewColumnPrefs — the reviewer's table view, remembered between reviews
 * (PLO-5119). Which columns are shown and whether entitlements are split by
 * type used to be plain component state, so every review opened on the same
 * fixed layout and the reviewer rebuilt it by hand each time.
 *
 * Deliberately NOT keyed per review, unlike useColumnWidths /
 * useInstructionsCollapsed: this is how a reviewer likes to work, not a fact
 * about one review, and re-asking per review is the problem the ticket is
 * about. It IS keyed per member, following state/members/frequentRequests —
 * signing out doesn't clear localStorage, so on a shared machine an unkeyed
 * preference would carry one reviewer's layout into the next one's session.
 *
 * Only explicit choices are stored. The entitlement-type default is derived
 * per review from that review's own entitlements, so a reviewer who never
 * touches the toggle gets it decided freshly each time rather than inheriting
 * whatever the last review happened to need.
 */
const storageKey = (memberId: string | null | undefined) =>
  `accessReview:columnPrefs:${memberId || "anon"}`;

const readPrefs = (key: string) => {
  try {
    return parseColumnPrefs(localStorage.getItem(key));
  } catch {
    return parseColumnPrefs(null);
  }
};

export const useReviewColumnPrefs = (hasEntitlementTypes: boolean) => {
  const memberId = useMemberAuthStore((s) => s.id);
  const key = storageKey(memberId);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    () => readPrefs(key).columns ?? DEFAULT_VISIBLE_COLUMNS,
  );
  // null until the reviewer makes an explicit choice — see
  // resolveSplitEntitlements, where null is what lets the per-review default
  // apply and false means "they turned it off".
  const [splitPref, setSplitPref] = useState<boolean | null>(
    () => readPrefs(key).splitEntitlementsByType,
  );

  // The store hydrates after first paint, so the member id — and with it the
  // key — can change under a mounted hook. Re-read rather than leaving the
  // anon defaults on screen.
  useEffect(() => {
    const prefs = readPrefs(key);
    setVisibleColumns(prefs.columns ?? DEFAULT_VISIBLE_COLUMNS);
    setSplitPref(prefs.splitEntitlementsByType);
  }, [key]);

  // Derived, not stored: no effect to fire, nothing to latch, and it re-decides
  // itself whenever the reviewer moves to a review with a different mix of
  // entitlement types.
  const splitEntitlementsByType = resolveSplitEntitlements(
    splitPref,
    hasEntitlementTypes,
  );

  const persist = useCallback(
    (columns: string[], split: boolean | null) => {
      try {
        localStorage.setItem(
          key,
          serializeColumnPrefs({
            columns: columnsForStorage(columns),
            splitEntitlementsByType: split,
          }),
        );
      } catch {
        // storage full / unavailable — keep the in-memory choice anyway
      }
    },
    [key],
  );

  /** Show/hide one column from the picker — an explicit choice, so persisted. */
  const toggleColumn = useCallback(
    (column: string) => {
      const next = toggleVisibleColumn(visibleColumns, column);
      if (next === visibleColumns) return;
      setVisibleColumns(next);
      persist(next, splitPref);
    },
    [persist, splitPref, visibleColumns],
  );

  /** Flip the splitter — also explicit, and it pins the per-review default off. */
  const setSplitEntitlementsByType = useCallback(
    (split: boolean) => {
      setSplitPref(split);
      // The reconciliation effect in ReviewDetail rewrites the entitlement
      // block right after this; persisting the pre-swap list is deliberate,
      // since columnsForStorage collapses that block either way.
      persist(visibleColumns, split);
    },
    [persist, visibleColumns],
  );

  return {
    visibleColumns,
    // Raw setter for the entitlement-split reconciliation, which derives
    // columns from the review's types rather than from anything the reviewer
    // asked for — those must not be written back as a preference.
    setVisibleColumns,
    toggleColumn,
    splitEntitlementsByType,
    setSplitEntitlementsByType,
  };
};

export default useReviewColumnPrefs;
